import { Box, Code, Heading, Text, VStack } from "@chakra-ui/react";
import { Element } from "react-scroll";
import CodeBlock from "./CodeBlock";
import DocsTable from "./DocsTable";

const SAMPLE_PLACE_ID = "019f1775-dc1f-7707-938f-ff043b1702be";

export default function PlacesDetailDocs() {
  return (
    <VStack align="stretch" spacing={10} w="full">
      <Element name="baseurl">
        <Box>
          <Heading as="h2">Base URL</Heading>
          <Text as="p" fontSize="lg" mt={5}>
            <Code wordBreak="break-all">https://api.potterstech.com/v1</Code>
          </Text>
        </Box>
      </Element>

      <Element name="endpoint">
        <Box>
          <Heading as="h2">Endpoint</Heading>
          <Text as="p" fontSize="lg" mt={5}>
            <Code wordBreak="break-all">
              GET /places/detail/&#123;place_id&#125;
            </Code>
          </Text>
          <Text as="p" fontSize="lg" mt={5}>
            <Text as="span" fontWeight="bold" textDecoration="none">
              Full URL:
            </Text>{" "}
            <Code wordBreak="break-all">
              https://api.potterstech.com/v1/places/detail/&#123;place_id&#125;
            </Code>
          </Text>
        </Box>
      </Element>

      <Element name="authentication">
        <Box>
          <Heading as="h2">Authentication</Heading>
          <Text as="p" fontSize="lg" mt={5}>
            <Code>X-Api-Key: &lt;YOUR_API_KEY&gt;</Code>
          </Text>
        </Box>
      </Element>

      <Element name="headers">
        <Box>
          <Heading as="h2">Headers</Heading>
          <DocsTable
            headers={["Header", "Required", "Description"]}
            rows={[
              ["Content-Type", "yes", "application/json"],
              ["X-Api-Key", "yes", "Your assigned API key"],
            ]}
          />
        </Box>
      </Element>

      <Element name="pathparameters">
        <Box>
          <Heading as="h2" mb={5}>
            Path Parameters
          </Heading>
          <DocsTable
            headers={["Parameter", "Type", "Required", "Description"]}
            rows={[
              [
                "place_id",
                "string",
                "yes",
                <>
                  Unique place identifier. This is the <Code>placeId</Code>{" "}
                  returned by the Places Autocomplete API.
                </>,
              ],
            ]}
          />
        </Box>
      </Element>

      <Element name="queryparameters">
        <Box>
          <Heading as="h2" mb={5}>
            Query Parameters
          </Heading>
          <DocsTable
            headers={["Parameter", "Type", "Required", "Description"]}
            rows={[
              [
                "fields",
                "string",
                "no",
                <>
                  Comma-separated list of fields to return. Use <Code>*</Code> to
                  return every field. Default: <Code>*</Code>
                </>,
              ],
            ]}
          />
        </Box>
      </Element>

      <Element name="requestexamples">
        <Box>
          <Heading as="h2" mb={5}>
            Request Examples
          </Heading>
          <Text as="b" mt={5}>
            All fields
          </Text>
          <CodeBlock containerProps={{ mt: 8 }}>
            {`GET https://api.potterstech.com/v1/places/detail/${SAMPLE_PLACE_ID}?fields=*`}
          </CodeBlock>

          <Text as="b" mt={8}>
            Selected fields only
          </Text>
          <CodeBlock containerProps={{ mt: 8 }}>
            {`GET https://api.potterstech.com/v1/places/detail/${SAMPLE_PLACE_ID}?fields=displayNames,location`}
          </CodeBlock>
        </Box>
      </Element>

      <Element name="responsebody">
        <Box>
          <Heading as="h2" mb={5}>
            Response Body
          </Heading>
          <CodeBlock containerProps={{ mt: 8 }}>
            {`{
  "id": "019f1775-dc1f-7707-938f-ff043b1702be",
  "location": "POINT(8.332066 4.977668)",
  "name": "place/019f1775-dc1f-7707-938f-ff043b1702be",
  "displayNames": [
    {
      "text": "Ishie St 45",
      "languageCode": "en"
    }
  ],
  "addressComponents": [
    { "text": "Nigeria", "type": "COUNTRY" },
    { "text": "Cross River", "type": "STATE" },
    { "text": "Calabar Municipal", "type": "LGA" },
    { "text": "Calabar", "type": "CITY" },
    { "text": "540281", "type": "POSTAL_CODE" },
    { "text": "45 Ishie St", "type": "STREET_NAME" },
    { "text": "45", "type": "STREET_NUMBER" }
  ]
}`}
          </CodeBlock>
        </Box>
      </Element>

      <Element name="responsefields">
        <Box>
          <Heading as="h2" mb={5}>
            Response Field Reference
          </Heading>

          <Text as="b" mt={5}>
            Top level
          </Text>
          <DocsTable
            headers={["Field", "Type", "Description"]}
            rows={[
              [
                "id",
                "string",
                "Unique place identifier. Same value as the place_id path parameter.",
              ],
              [
                "name",
                "string",
                <>
                  Fully qualified resource name, in the form{" "}
                  <Code>place/&#123;id&#125;</Code>
                </>,
              ],
              [
                "location",
                "string",
                <>
                  Geographic position as a WKT point,{" "}
                  <Code>POINT(longitude latitude)</Code>. Longitude comes first.
                </>,
              ],
              [
                "displayNames",
                "array",
                "Localised display names for the place",
              ],
              [
                "addressComponents",
                "array",
                "Structured parts of the place's address",
              ],
            ]}
          />

          <Text as="b" mt={8}>
            displayNames[]
          </Text>
          <DocsTable
            headers={["Field", "Type", "Description"]}
            rows={[
              ["text", "string", "The display name"],
              [
                "languageCode",
                "string",
                "BCP-47 language code the name is written in, for example en",
              ],
            ]}
          />

          <Text as="b" mt={8}>
            addressComponents[]
          </Text>
          <DocsTable
            headers={["Field", "Type", "Description"]}
            rows={[
              ["text", "string", "Value of this address component"],
              [
                "type",
                "string",
                <>
                  Kind of component. Values observed include{" "}
                  <Code>COUNTRY</Code>, <Code>STATE</Code>, <Code>LGA</Code>,{" "}
                  <Code>CITY</Code>, <Code>POSTAL_CODE</Code>,{" "}
                  <Code>STREET_NAME</Code> and <Code>STREET_NUMBER</Code>. The
                  set of types varies by place and by country.
                </>,
              ],
            ]}
          />
        </Box>
      </Element>

      <Element name="examplerequests">
        <Box>
          <Heading as="h2" mb={5}>
            Example Requests
          </Heading>

          <Text as="b" mt={5}>
            cURL
          </Text>
          <CodeBlock variant="dark" containerProps={{ mt: 8 }}>
            {`curl -X GET \\
  "https://api.potterstech.com/v1/places/detail/${SAMPLE_PLACE_ID}?fields=*" \\
  -H "x-api-key: <YOUR_API_KEY>"`}
          </CodeBlock>

          <Text as="b" mt={8}>
            JavaScript
          </Text>
          <CodeBlock variant="dark" containerProps={{ mt: 8 }}>
            {`const placeId = "${SAMPLE_PLACE_ID}";

const response = await fetch(
  \`https://api.potterstech.com/v1/places/detail/\${placeId}?fields=*\`,
  {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": "<YOUR_API_KEY>",
    },
  }
);

const data = await response.json();
console.log(data);`}
          </CodeBlock>

          <Text as="b" mt={8}>
            Python
          </Text>
          <CodeBlock variant="dark" containerProps={{ mt: 8 }}>
            {`import requests

place_id = "${SAMPLE_PLACE_ID}"
url = f"https://api.potterstech.com/v1/places/detail/{place_id}"

response = requests.get(
    url,
    params={"fields": "*"},
    headers={
        "Content-Type": "application/json",
        "x-api-key": "<YOUR_API_KEY>",
    },
)

print(response.json())`}
          </CodeBlock>
        </Box>
      </Element>

      <Element name="statuscodes">
        <Box>
          <Heading as="h2" mb={5}>
            Status Codes
          </Heading>
          <DocsTable
            headers={["Code", "Meaning"]}
            rows={[
              ["200", "Success. The place was found and returned."],
              ["400", "Malformed request, for example an invalid place_id."],
              [
                "401",
                <>
                  Missing or invalid API key. Responds with{" "}
                  <Code>
                    &#123;"message":"Missing API Key (x-api-key)"&#125;
                  </Code>{" "}
                  when the header is absent.
                </>,
              ],
              ["403", "The API key is not permitted to use this endpoint."],
              ["404", "No place exists with the supplied place_id."],
              ["429", "Rate limit exceeded."],
              ["5xx", "Server error. Retry the request."],
            ]}
          />
        </Box>
      </Element>

      <Element name="notes">
        <Box>
          <Heading as="h2">Notes</Heading>
          <Text as="div" fontSize="lg" mt={5}>
            <ul style={{ paddingLeft: "1.25rem" }}>
              <li>
                A <Code>place_id</Code> is obtained from the{" "}
                <Code>placeId</Code> field of a Places Autocomplete response.
              </li>
              <li>
                <Code>fields=*</Code> returns every available field and is the
                default when the parameter is omitted.
              </li>
              <li>
                <Code>location</Code> is a WKT point with{" "}
                <Text as="span" fontWeight="bold">
                  longitude first
                </Text>
                , which is the reverse of the lat/lng ordering used by the
                Geocode APIs.
              </li>
              <li>
                Which <Code>addressComponents</Code> are present depends on the
                place and the country, so read them by <Code>type</Code> rather
                than by position.
              </li>
            </ul>
          </Text>
        </Box>
      </Element>
    </VStack>
  );
}
